import { FC, ReactNode, useRef, useState } from 'react';
import {
  CollectionConstraintType,
  ContractFormData as Form,
} from '../../../../types/contracts';
import { FormProvider, useForm } from 'react-hook-form';
import styles from './styles.module.css';
import General from './General';
import SupplierCompany from './SupplierCompany';
import ContractorCompany from './ContractorCompany';
import WastePointCompany from './WastePointCompany';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAngleDown,
  faAngleUp,
  faPlus,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import CarCategoriesForm from './CarCategoriesForm';
import CollectionConstraintForm from './CollectionConstraintForm';
import SkillAndQualificationForm from './SkillAndQualificationForm';
import WasteItemForm from './WasteItemForm';
import Button from '../../../../components/common/Button';
import { logger } from '../../../../services/logger';
import FooterControl from '../../../../components/common/FooterControl';
import FooterLayout from '../../../../components/FooterLayout';
import { format, parse, subDays } from 'date-fns';
import { WasteItem } from '../../../../types/others';
import { SkillLevel } from '../../../../types/driver';
import classNames from 'classnames';
import Modal from '../../../../components/common/Modal';

type Props = {
  initState: Form;
  wasteItems: WasteItem[] | undefined;
  isEditable?: boolean;
  constraintTypes: CollectionConstraintType[] | undefined;
  skillLevels: SkillLevel[] | undefined;
  onSubmit: (form: Form) => void;
  onCancel: () => void;
};
const editMessageDateFormat = 'M月d日';

const ContractForm: FC<Props> = ({
  initState,
  wasteItems: apiWasteItems,
  isEditable = true,
  constraintTypes,
  skillLevels,
  onCancel,
  onSubmit,
}) => {
  const form = useForm<Form>({
    values: {
      ...initState,
    },
    mode: 'onBlur',
  });
  const { watch, setValue, getValues, handleSubmit, unregister } = form;
  const wasteItemInfos = watch('wasteItemsInfo');
  const addWasteItemInfos = () => {
    const newValue = [
      ...wasteItemInfos,
      {
        wasteItemId: apiWasteItems?.[0]?.wasteItemId ?? null,
        amountOfWaste: null,
        wasteItemName: '',
        disposalSiteCompanyId: null,
        specificTimeStartAt: '',
        specificTimeEndAt: '',
      },
    ];
    setValue('wasteItemsInfo', newValue);
  };
  const deleteWasteItemInfo = (index: number) => {
    const wasteItemKey = `wasteItemsInfo.${wasteItemInfos.length - 1}` as const;
    const newValue = [
      ...wasteItemInfos.slice(0, index),
      ...wasteItemInfos.slice(index + 1),
    ];
    unregister(wasteItemKey);
    setValue('wasteItemsInfo', newValue);
  };
  const formatTime = (data: Form) => {
    if (data.collectionConstraintTimeStart)
      data.collectionConstraintTimeStart = format(
        parse(data.collectionConstraintTimeStart, 'HH:mm', new Date()),
        'HH:mm'
      );
    if (data.collectionConstraintTimeEnd)
      data.collectionConstraintTimeEnd = format(
        parse(data.collectionConstraintTimeEnd, 'HH:mm', new Date()),
        'HH:mm'
      );
    data.wasteItemsInfo.map((wasteItem, index) => {
      if (wasteItem.specificTimeStartAt)
        data.wasteItemsInfo[index].specificTimeStartAt = format(
          parse(wasteItem.specificTimeStartAt, 'HH:mm', new Date()),
          'HH:mm'
        );
      if (wasteItem.specificTimeEndAt)
        data.wasteItemsInfo[index].specificTimeEndAt = format(
          parse(wasteItem.specificTimeEndAt, 'HH:mm', new Date()),
          'HH:mm'
        );
    });
    return data;
  };
  const [doesHideOthers, setDoesHideOthers] = useState(false);
  const submitHandler = handleSubmit(
    (data, e) => {
      e?.preventDefault();
      onSubmit(formatTime(data));
    },
    (e) => {
      onModal();
      logger.error(e);
    }
  );
  const [isToastPopUp, setIsToastPopUp] = useState(false);
  const formRef = useRef<HTMLFormElement | null>(null);

  const [isInvalidModalOpen, setIsInvalidModalOpen] = useState(false);
  const onModal = () => setIsInvalidModalOpen(true);
  const onModalClose = () => setIsInvalidModalOpen(false);
  const yesterday = subDays(new Date(), 1);
  const editAlertMessage = isEditable ? undefined : (
    <>
      <div>
        {`${format(yesterday, editMessageDateFormat)}`}
        までの日程で配車済みのため、変更できません。
      </div>
      <div>変更したい場合は、複製後、受注入力を新たに登録してください。</div>
    </>
  );
  return (
    <FormProvider {...form}>
      <form className={styles.host} onSubmit={submitHandler} ref={formRef}>
        <FooterLayout
          main={
            <div
              className={classNames(
                styles.container,
                isToastPopUp && styles.isToastPopUp
              )}
            >
              <div className={styles.form}>
                <h3 className={styles.formTitle}>契約内容</h3>
                <Section>
                  <General />
                </Section>
                <Section>
                  <SupplierCompany />
                </Section>
                <Section>
                  <ContractorCompany />
                </Section>
                <Section>
                  <WastePointCompany />
                </Section>
                <HorizontalRule />
                <div>
                  {doesHideOthers || (
                    <>
                      <Section>
                        <CarCategoriesForm />
                      </Section>
                      <div>
                        <CollectionConstraintForm
                          constraintTypes={constraintTypes}
                        />
                        <SkillAndQualificationForm skillLevels={skillLevels} />
                      </div>
                    </>
                  )}
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() => setDoesHideOthers(!doesHideOthers)}
                    className={styles.otherFormsOpener}
                  >
                    <FontAwesomeIcon
                      icon={doesHideOthers ? faAngleDown : faAngleUp}
                      className={styles.arrow}
                    />
                    その他の回収条件を{doesHideOthers ? '表示' : '非表示'}
                  </button>
                </div>
              </div>
              {!!wasteItemInfos.length &&
                wasteItemInfos.map((_wi, i) => (
                  <div key={i} className={styles.form}>
                    <h3 className={styles.formTitle}>{i + 1}.廃棄品目情報</h3>
                    {wasteItemInfos.length > 1 && (
                      <button
                        type="button"
                        className={styles.deleteWasteItemInfo}
                        onClick={() => deleteWasteItemInfo(i)}
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    )}
                    <WasteItemForm index={i} wasteItems={apiWasteItems} />
                  </div>
                ))}
              <button
                type="button"
                onClick={addWasteItemInfos}
                className={styles.addWasteItemsInfo}
              >
                <FontAwesomeIcon icon={faPlus} className={styles.icAdd} />
              </button>
            </div>
          }
          footer={
            <FooterControl setIsNeedBottomMargin={(v) => setIsToastPopUp(v)}>
              <div className={styles.controlButton} onClick={onCancel}>
                <Button type="button" variants={['fit', 'cancel']}>
                  キャンセル
                </Button>
              </div>
              <div className={styles.controlButton}>
                <Button
                  type="button"
                  variants={['fit']}
                  disabled={!isEditable}
                  onClick={() => {
                    if (!getValues('goesStraight')) {
                      // 積替保管の場合、契約先処分場と到着予定時間の値をnullとする
                      wasteItemInfos.forEach((e, count) => {
                        setValue(
                          `wasteItemsInfo.${count}.specificTimeStartAt`,
                          null
                        );
                        setValue(
                          `wasteItemsInfo.${count}.specificTimeEndAt`,
                          null
                        );
                        setValue(
                          `wasteItemsInfo.${count}.disposalSiteCompanyId`,
                          null
                        );
                      });
                    }
                    formRef.current?.requestSubmit();
                  }}
                >
                  確定
                </Button>
              </div>
            </FooterControl>
          }
          error={editAlertMessage}
        />
      </form>
      {isInvalidModalOpen && (
        <Modal onRequestClose={onModalClose}>
          <div className={styles.modalHead}>入力項目にエラーがあります</div>
          <div className={styles.modalContent}>
            <div className={styles.modalText}>
              入力された受注情報にエラーがあります。
              <br />
              入力内容を確認してください。
            </div>
            <div className={styles.modalButtons}>
              <div className={styles.modalButton}>
                <Button type="button" onClick={onModalClose} variants={['fit']}>
                  確認
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </FormProvider>
  );
};

type LayoutProps = {
  children: ReactNode;
};
const Section: FC<LayoutProps> = ({ children }) => {
  return <div className={styles.section}>{children}</div>;
};
const HorizontalRule = () => {
  return <hr className={styles.hr} />;
};

export default ContractForm;
